import React from 'react';
import { makeStyles } from '@mui/styles';
import { useController } from 'react-hook-form';
import { InputNumber } from 'antd';
import clsx from 'clsx';

import { 
  CreditRisk, 
  DealerType, 
  fixedRiskText,
} from '@types';
import {
  IGrid,
  InitialCard,
  InitialRow,
  ManyTagsInput,
} from '@components';

const MAX_AMOUNT_VALUE = 1000000000;

interface Props {
  risk: CreditRisk;
  isFixed: boolean;
  labelGrid?: IGrid;
  valueGrid?: IGrid;
}

export const RiskInitial = ({ risk, labelGrid, valueGrid }: Props) => {
  const { credit } = risk;
  const { user } = credit;

  const {
    field: { value: requestAmount, onChange: onRequestAmountChange },
  } = useController({ name: 'requestAmount' });

  const handleRequestAmountChange = (value: number | null) => {
    if (value && value > MAX_AMOUNT_VALUE) return;
    
    const newValue = (value || 0) >= MAX_AMOUNT_VALUE ? MAX_AMOUNT_VALUE : value;

    onRequestAmountChange(newValue);
  };

  const crops = credit.creditCrops.map(crop => crop.financedCrop?.nameMx).join(', ');
  const cls = useStyles();

  return (
    <InitialCard left="General information" autoHeight extraPadding
      lockText={risk.isFixed ? fixedRiskText : null}>
      <div className={cls.box}>
        <InitialRow
          title="Credit code"
          labelGrid={labelGrid}
          valueGrid={valueGrid}
        >{credit.code}</InitialRow>

        <InitialRow
          title="Accredited"
          labelGrid={labelGrid}
          valueGrid={valueGrid}
        >{credit.accreditedName}</InitialRow>

        <InitialRow title="Buyers:" labelGrid={labelGrid} valueGrid={valueGrid}>
          <ManyTagsInput
            source="buyerIds"
            reference={`dealer/by-type/${DealerType.BUYER}`}
          />
        </InitialRow>

        <InitialRow title="Retailers:" labelGrid={labelGrid} valueGrid={valueGrid}>
          <ManyTagsInput
            source="retailerIds"
            reference={`dealer/by-type/${DealerType.RETAILER}`}
          />
        </InitialRow>

        <InitialRow title="Associates:" labelGrid={labelGrid} valueGrid={valueGrid}>
          <ManyTagsInput
            source="associateIds"
            reference={`dealer/by-type/${DealerType.ASSOCIATE}`}
          />
        </InitialRow>

        <InitialRow
          title="State"
          labelGrid={labelGrid}
          valueGrid={valueGrid}
        >
          {user.state?.name}
        </InitialRow>

        <InitialRow
          title="Crop"
          labelGrid={labelGrid}
          valueGrid={valueGrid}
        >
          {crops}
        </InitialRow>
        
        <InitialRow
          title="Amount requested"
          labelGrid={labelGrid}
          valueGrid={valueGrid}
          className={cls.requested}
        >
          <div className={cls.amount}>
            <InputNumber
              min={0}
              max={MAX_AMOUNT_VALUE}
              value={requestAmount || 0}
              formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => +(value?.replace(/\$\s?|(,*)/g, '') || 0)}
              onChange={handleRequestAmountChange}
              bordered={false}
              className={cls.fieldNumber}
              controls={false}
            />
          </div>
        </InitialRow>

        <InitialRow
          title="Accredited"
          labelGrid={labelGrid}
          valueGrid={valueGrid}
        >{credit?.user?.sourceEn || '–'}</InitialRow>

        <div className={cls.clientType}>
          <ClientTypeIndicator isCompany={user.isCompany} />
        </div>
      </div>
    </InitialCard>
  );
};

const ClientTypeIndicator = ({ isCompany }: {isCompany: boolean}) => {
  const cls = useStyles();

  return (
    <div className={cls.clientType}>
      <div className={cls.clientTypeIndicator}>
        <div className={clsx(cls.clientTypeIcon, !isCompany && cls.clientTypeIconActive)} />
        <div className={cls.clientTypeText}>PF - Persona Física</div>
      </div>
      <div className={cls.clientTypeIndicator}>
        <div className={clsx(cls.clientTypeIcon, isCompany && cls.clientTypeIconActive)} />
        <div className={cls.clientTypeText}>PM - Persona Moral</div>
      </div>
    </div>
  );
};

const useStyles = makeStyles({
  clientType: {
    marginTop: 16,
    display: 'flex',
    gap: 20,
    alignItems: 'center',
  },
  clientTypeIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 12px',
    borderRadius: 2,
    border: '1px solid #042E6B',
    background: '#F5F7FA',
  },
  clientTypeIcon: {
    border: '1px solid #042E6B',
    width: 20,
    height: 20,
    borderRadius: 2,
  },
  clientTypeText: {
    color: '#042E6B',
    fontSize: 14,
    fontWeight: 500,
  },
  clientTypeIconActive: {
    background: '#042E6B',
  },
  box: {
    marginTop: 15,
    fontFamily: 'var(--font-primary)',
    color: '#272727',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,

    '& > .ant-row': {
      marginTop: 0,
    },

    '& > div': {
      minHeight: 32,
    },
  },
  requested: {
    marginTop: '12px !important',
  },
  amount: {
    '& p': {
      display: 'none',
    },
  },
  amountValue: {
    fontSize: 12,
    color: '#5b5b5b',
  },
  error: {
    color: 'red',
  },
  field: {
    height: 32,
    marginTop: '0px !important',
    width: '100%',

    '& input': {
      fontSize: 18,
      padding: '0px !important',
    },

    '& div': {
      marginTop: 0,
    },

    '& p': {
      display: 'none',
    },
  },
  fieldNumber: {
    height: 32,
    marginTop: '0px !important',
    width: '100%',
    borderBottom: '1px solid rgba(0, 42, 119, 0.10)',
    
    '& div': {
      marginTop: 0,
    },

    '&:hover': {
      borderBottom: '1px solid rgba(0, 42, 119, 0.10)',
    },

    '& input': {
      fontSize: 18,
      padding: '0px !important',
    },
  },
  '.MuiInputBase-formControl': {
    marginTop: '0px !important',
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 600,
    color: '#000',
  },
});
